
import React, { useState, useEffect, useCallback } from 'react';
import { supabase, logoutUser } from './supabaseClient';
import { Session } from '@supabase/supabase-js';
import { Dashboard } from './components/Dashboard';
import { ShiftEntry } from './components/ShiftEntry';
import { HistoryView } from './components/HistoryView';
import { Feedback } from './components/Feedback';
import { ProfileView } from './components/ProfileView';
import { Auth } from './components/Auth';
import { InstallPWA } from './components/InstallPWA';
import { NewsBanner } from './components/NewsBanner';
import { PasswordUpdate } from './components/PasswordUpdate';
import { ViewMode, Shift, ShiftInput, UserProfile, Story, ShiftType, GarageCheckin } from './types';
import { GarageAgentView } from './components/GarageAgentView';
import { GarageDriverCard } from './components/GarageDriverCard';
import { Stories } from './components/Stories';
import { AdvanceCalculator } from './components/AdvanceCalculator';
import { SupportCenter } from './components/SupportCenter';
import { FleetView } from './components/FleetView';
import { LayoutDashboard, PlusCircle, History, MessageSquare, User, LogOut, Loader2, Menu, X, Trophy, BookOpen, Wallet, BarChart2, ExternalLink, Play, Share2, Volume2, FileText, Headset, Car, ChevronLeft, ShieldCheck, Lock, Crown } from 'lucide-react';
import { YOUTUBE_TRACKS } from './ytTracks';
import { processShiftGamification, trackFeatureUsage } from './services/gamificationService';
import { PerformanceView } from './components/PerformanceView';
import { RankingView } from './components/RankingView';
import { AIAvatarGenerator } from './components/AIAvatarGenerator';
import { PartnersHub } from './components/PartnersHub';
import { RhinoService } from './services/RhinoService';
import { calculateEarnings } from './services/calculationService';
import { PwaTipModal } from './components/PwaTipModal';
import { SubscriptionModal } from './components/SubscriptionModal';
import { TuesdayShieldModal } from './components/TuesdayShieldModal';
import { ZereSeuAnoModal } from './components/ZereSeuAnoModal';
import { UpdateBanner } from './components/UpdateBanner';
import { APP_VERSION } from './config';
import { CheckCircle2 } from 'lucide-react';
import { DottedSurface } from './components/ui/dotted-surface';

// --- GLOBALS & ANIMATIONS ---
const VIP_EMAILS = [
    'tiagoromao25@gmail.com',
    'anapaulaautentica@gmail.com',
    'eenokibara@gmail.com',
    'josesantostimes@gmail.com',
    'ana.ubersp@gmail.com',
    'nelsandromatias@hotmail.com',
    'mmmontefuscobernardo@gmail.com',
    'mmontefuscobernardo@gmail.com'
];

const isVipEmail = (email: string | undefined): boolean => {
    if (!email) return false;
    const lowerEmail = email.toLowerCase();
    // Special case for eenokibara as requested (gmail or yahoo)
    if (lowerEmail.startsWith('eenokibara@')) return true;
    return VIP_EMAILS.includes(lowerEmail);
};

const GlobalStyles = () => (
    <style>{`
    @keyframes pulse-gentle {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.02); opacity: 0.9; }
    }
    .animate-pulse-gentle {
      animation: pulse-gentle 2s infinite ease-in-out;
    }
  `}</style>
);

// --- DATA MAPPERS ---

// Helper to handle mixed data types (string "1.200,50", "200", or number 200.50)
// This fixes the issue where December/January data might be saved as text/comma format
const safeFloat = (value: any): number => {
    if (typeof value === 'number') return value;
    if (!value) return 0;

    if (typeof value === 'string') {
        // Remove currency symbols R$ and spaces
        let clean = value.replace(/[R$\s]/g, '');

        // NEW ROBUST PARSING FOR PT-BR:
        // Case A: Mixed (1.200,50) -> remove dot, swap comma
        if (clean.includes('.') && clean.includes(',')) {
            clean = clean.replace(/\./g, '').replace(',', '.');
        }
        // Case B: Only comma (120,50) -> swap comma
        else if (clean.includes(',') && !clean.includes('.')) {
            clean = clean.replace(',', '.');
        }
        // Case C: Only dot (1.200) -> 
        // We need to decide if dot is thousands or decimal.
        // In BR context for large numbers, it's usually thousands.
        // If there's exactly 3 digits after the dot, it's very likely a thousands separator.
        else if (clean.includes('.') && !clean.includes(',')) {
            const parts = clean.split('.');
            if (parts[parts.length - 1].length === 3) {
                // Treat as thousands: 1.200 -> 1200
                clean = clean.replace(/\./g, '');
            } else {
                // Treat as decimal: 1.2 -> 1.2 (stays)
            }
        }

        const parsed = parseFloat(clean);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

// Helper to ensure YYYY-MM-DD format regardless of input (ISO string, etc)
const sanitizeDate = (dateStr: string | undefined): string => {
    if (!dateStr) return new Date().toISOString().split('T')[0];

    // Handle DD/MM/YYYY or DD-MM-YYYY
    if (dateStr.includes('/') || (dateStr.includes('-') && dateStr.indexOf('-') === 2)) {
        const parts = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('-');
        if (parts.length === 3 && parts[2].length === 4) {
            // Convert to YYYY-MM-DD
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
    }

    // Normal ISO leading part
    return dateStr.substring(0, 10);
};

// Convert Database Object (snake_case) to Frontend Object (camelCase)
const fromDbShift = (db: any, generation: 'OLD' | 'NEW' = 'OLD'): Shift => {
    // 1. Calculate main values using safeFloat to catch text-formatted numbers
    const totalEarnings = safeFloat(db.total_earnings || db.totalEarnings);
    const appTips = safeFloat(db.app_tips || db.appTips || 0);
    const outsideTips = safeFloat(db.tips || db.outside_tips || db.outsideTips || 0);

    // 2. Data Repair & Normalization
    // Use uppercase and default to WORK
    let type = (db.type || 'WORK').toUpperCase() as ShiftType;
    let noShow = db.no_show || false;

    // AUTO-CORRECTION A: If it has earnings but type is not WORK, fix it
    if ((totalEarnings > 0 || appTips > 0 || outsideTips > 0) && type !== 'WORK') {
        type = 'WORK';
        noShow = false;
    }

    // AUTO-CORRECTION B: Ensure noShow flag matches the type
    if (type === 'NO_SHOW') noShow = true;
    if (type === 'WORK' || type === 'OFF') noShow = false;

    // 3. Handle Breakdown (essential for charts)
    // If breakdown is missing or bilingual pay is zero but hours are present, RECALCULATE
    let breakdown = db.breakdown;
    const hasBilingualHours = db.bilingual_start && db.bilingual_end;
    const needsRecalculation = !breakdown ||
        (breakdown.basePay === 0 && breakdown.nightPay === 0 && totalEarnings > 0) ||
        (hasBilingualHours && (breakdown.bilingualPay === 0 || !breakdown.bilingualPay));

    if (needsRecalculation && type === 'WORK') {
        // Use the robust calculation service to restore the missing details
        const calc = calculateEarnings(
            sanitizeDate(db.date),
            db.start_time || db.startTime || '06:00',
            db.end_time || db.endTime || '18:00',
            db.is_holiday || false,
            db.bilingual_start || db.bilingualStart,
            db.bilingual_end || db.bilingualEnd,
            generation
        );
        breakdown = calc.breakdown;
    } else if (!breakdown) {
        breakdown = {
            basePay: totalEarnings,
            nightPay: 0,
            bilingualPay: 0
        };
    }

    return {
        id: db.id,
        user_id: db.user_id,
        type: type,
        noShow: noShow,
        date: sanitizeDate(db.date),
        startTime: db.start_time || db.startTime || '06:00',
        endTime: db.end_time || db.endTime || '18:00',
        isHoliday: db.is_holiday || false,
        bilingualStart: db.bilingual_start || db.bilingualStart,
        bilingualEnd: db.bilingual_end || db.bilingualEnd,
        totalEarnings: totalEarnings,
        totalHours: safeFloat(db.total_hours || db.totalHours),
        appTips: appTips,
        tips: outsideTips,
        breakdown: breakdown,
        expenses: db.expenses || [],
        extraEarnings: db.extra_earnings || db.extraEarnings || [],
        unpaidBreakMinutes: safeFloat(db.unpaid_break_minutes || db.unpaidBreakMinutes || 0),
        plannedStartTime: db.planned_start_time || db.plannedStartTime || undefined,
        plannedEndTime: db.planned_end_time || db.plannedEndTime || undefined,
        vehiclePlate: db.vehicle_plate || db.vehiclePlate || undefined,
        carReadyOnTime: db.car_ready_on_time !== undefined ? db.car_ready_on_time : (db.carReadyOnTime !== undefined ? db.carReadyOnTime : true),
        carArrivalTime: db.car_arrival_time || "",
        loginAppTime: db.login_app_time || "",
        loginTelegramTime: db.login_telegram_time || "",
        logoutTelegramTime: db.logout_telegram_time || "",
        generation: db.generation || generation
    };
};

// Convert Frontend Object (camelCase) to Database Object (snake_case)
const toDbShift = (shift: Shift) => ({
    id: shift.id,
    user_id: shift.user_id,
    type: shift.type,
    no_show: shift.noShow,
    date: shift.date,
    start_time: shift.startTime,
    end_time: shift.endTime,
    is_holiday: shift.isHoliday,
    bilingual_start: shift.bilingualStart,
    bilingual_end: shift.bilingualEnd,
    total_earnings: shift.totalEarnings,
    total_hours: shift.totalHours,
    // Combine appTips and tips into the single 'tips' column available in the schema
    tips: (shift.tips || 0) + (shift.appTips || 0),
    breakdown: shift.breakdown,
    expenses: shift.expenses,
    extra_earnings: shift.extraEarnings,
    unpaid_break_minutes: shift.unpaidBreakMinutes || 0,
    planned_start_time: shift.plannedStartTime || null,
    planned_end_time: shift.plannedEndTime || null,
    vehicle_plate: shift.vehiclePlate || null,
    car_ready_on_time: shift.carReadyOnTime ?? true,
    car_arrival_time: shift.carArrivalTime || null,
    login_app_time: shift.loginAppTime || null,
    login_telegram_time: shift.loginTelegramTime || null,
    logout_telegram_time: shift.logoutTelegramTime || null,
    generation: shift.generation || null
});

// Cache provider token in local storage (useful because Supabase loses it on refresh)
const storeProviderToken = (session: any) => {
    if (session?.provider_token) {
        localStorage.setItem('google_provider_token', session.provider_token);
        const expiresAt = Date.now() + 3600 * 1000;
        localStorage.setItem('google_provider_token_expires_at', String(expiresAt));
    }
};

const App: React.FC = () => {
    const [session, setSession] = useState<Session | null>(null);
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [view, setView] = useState<ViewMode>(ViewMode.DASHBOARD);
    const [viewHistory, setViewHistory] = useState<ViewMode[]>([ViewMode.DASHBOARD]);
    const [tourStep, setTourStep] = useState(0);

    const getProviderToken = () => {
        const token = session?.provider_token || localStorage.getItem('google_provider_token');
        const expiresAtStr = localStorage.getItem('google_provider_token_expires_at');
        if (!token) return null;
        if (expiresAtStr) {
            const expiresAt = parseInt(expiresAtStr, 10);
            if (Date.now() > expiresAt) {
                localStorage.removeItem('google_provider_token');
                localStorage.removeItem('google_provider_token_expires_at');
                return null;
            }
        }
        return token;
    };

    const handleConnectGoogle = async () => {
        try {
            const redirectUrl = window.location.origin + '?view=desempenho';
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: redirectUrl,
                    scopes: 'https://www.googleapis.com/auth/gmail.readonly',
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    },
                },
            });
            if (error) throw error;
        } catch (err: any) {
            console.error("Google Connection Error:", err);
            alert("Erro ao conectar conta Google: " + err.message);
        }
    };

    const [isGlobalSubModalOpen, setIsGlobalSubModalOpen] = useState(false);
    const [globalSubFeatureName, setGlobalSubFeatureName] = useState<string | undefined>();
    const [showChangelogModal, setShowChangelogModal] = useState(false);
    const [garageCheckins, setGarageCheckins] = useState<GarageCheckin[]>([]);

    // Detect garage agent route
    const isGarageRoute = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('view') === 'garagem';









    // Expose alarm trigger globally for testing
    useEffect(() => {
        (window as any).dispatchAlarm = () => {
            const randomTrack = YOUTUBE_TRACKS[Math.floor(Math.random() * YOUTUBE_TRACKS.length)];
            setActiveAlarmTrack(randomTrack);
            setHasInteractedWithAlarm(false);
            setIsAlarmRinging(true);
        };
    }, []);



    const changeView = (newView: ViewMode, addToHistory = true) => {
        if (newView === ViewMode.FINANCE) {
            // Special case: Finance view now shows the summary logic requested
        }
        
        // Navigate back if going to dashboard from history
        if (newView === ViewMode.DASHBOARD && viewHistory.length > 1 && addToHistory) {
            const newHistory = viewHistory.slice(0, -1);
            setViewHistory(newHistory);
            setView(newHistory[newHistory.length - 1]);
        } else {
            setView(newView);
            if (addToHistory && newView !== view) {
                setViewHistory([...viewHistory, newView]);
            }
        }
        if (session?.user?.id) {
            // Track usage for specific views
            if (newView === ViewMode.RANKING) trackFeatureUsage(session.user.id, 'RANKING_VIEW');
        }
        setIsSidebarOpen(false);
    };

    const handleRequirePremium = (featureName?: string) => {
        setGlobalSubFeatureName(featureName);
        setIsGlobalSubModalOpen(true);
    };
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formInitialData, setFormInitialData] = useState<Partial<ShiftInput> | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

    // Check if subscription has expired
    const isSubscriptionActive = (() => {
        if (!userProfile?.is_premium) return false;
        const endDate = userProfile.subscription_end_date || (userProfile as any).subscription_expires_at;
        if (!endDate) return true; // No end date = active (auto-renewal)
        return new Date(endDate) > new Date();
    })();

    const daysUntilExpiration = (() => {
        const endDate = userProfile?.subscription_end_date || (userProfile as any)?.subscription_expires_at;
        if (!userProfile?.is_premium || !endDate || !isSubscriptionActive) return null;
        const now = new Date();
        const end = new Date(endDate);
        const diffTime = end.getTime() - now.getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    })();

    const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration >= 0 && daysUntilExpiration <= 5;

    const isPremiumOrVip = isSubscriptionActive || isVipEmail(session?.user?.email);

    // ── Subscription enforcement deadline: June 10, 2026 ──────────────────
    const BLOCK_DEADLINE = new Date('2026-06-10T00:00:00-03:00');
    const isAfterBlockDeadline = new Date() >= BLOCK_DEADLINE;
    const daysUntilDeadline = Math.max(0, Math.ceil((BLOCK_DEADLINE.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)));
    // Show the warning banner to non-subscribers until the deadline
    const showSubscriptionDeadlineBanner = !isPremiumOrVip && !isAfterBlockDeadline && !!userProfile;

    // ── 7-Day Free Trial for New Users ──
    const isWithinTrialPeriod = (() => {
        if (!userProfile?.created_at) return false;
        const registrationDate = new Date(userProfile.created_at);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return registrationDate > sevenDaysAgo;
    })();

    const trialDaysRemaining = (() => {
        if (!userProfile?.created_at) return null;
        const registrationDate = new Date(userProfile.created_at);
        const expirationDate = new Date(registrationDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        const now = new Date();
        const diffTime = expirationDate.getTime() - now.getTime();
        return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    })();

    const isGoogleConnected = session?.app_metadata?.provider === 'google' ||
        session?.user?.app_metadata?.provider === 'google' ||
        (session as any)?.user?.identities?.some((id: any) => id.provider === 'google') || false;

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [isRecoveryMode, setIsRecoveryMode] = useState(false);
    const [isAlarmRinging, setIsAlarmRinging] = useState(false);
    const [isPrivacyMode, setIsPrivacyMode] = useState(() => localStorage.getItem('driver_score_privacy_mode') === 'true');
    const [showRhinoError, setShowRhinoError] = useState(false);
    const [viewingProfileId, setViewingProfileId] = useState<string | null>(null);
    const [activeAlarmTrack, setActiveAlarmTrack] = useState<any>(null);
    const [hasInteractedWithAlarm, setHasInteractedWithAlarm] = useState(false);
    const [showPwaTip, setShowPwaTip] = useState(false);
    const [isTuesdayModalOpen, setIsTuesdayModalOpen] = useState(false);
    const [isZereSeuAnoModalOpen, setIsZereSeuAnoModalOpen] = useState(false);
    const [theme, setTheme] = useState<'dark' | 'light'>(
        () => {
            const saved = localStorage.getItem('driver_score_theme');
            return saved === 'light' ? 'light' : 'dark';
        }
    );

    const cycleTheme = () => {
        setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
    };

    const getThemeIcon = () => {
        if (theme === 'dark') return '🌙';
        return '☀️';
    };

    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove('theme-light', 'theme-worldcup', 'dark');
        if (theme === 'light') {
            root.classList.add('theme-light');
        } else {
            root.classList.add('dark');
        }
        localStorage.setItem('driver_score_theme', theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem('driver_score_privacy_mode', isPrivacyMode.toString());
    }, [isPrivacyMode]);

    // Tuesday Shield Logic
    useEffect(() => {
        if (isLoading || !userProfile || isPremiumOrVip) return;

        const now = new Date();
        if (now.getDay() !== 2) return; // Only on Tuesdays

        const hour = now.getHours();
        let currentBlock = '';
        if (hour < 15) currentBlock = 'morning';
        else if (hour < 20) currentBlock = 'afternoon';
        else currentBlock = 'night';

        const todayDateStr = now.toISOString().split('T')[0];
        const lastShownDataStr = localStorage.getItem('driver_score_tuesday_modal_last_shown');
        
        let shouldShow = false;
        
        if (!lastShownDataStr) {
            shouldShow = true;
        } else {
            try {
                const lastShownData = JSON.parse(lastShownDataStr);
                if (lastShownData.date !== todayDateStr || lastShownData.block !== currentBlock) {
                    shouldShow = true;
                }
            } catch (e) {
                shouldShow = true;
            }
        }

        const isTuesdayTourDisabled = localStorage.getItem('driver_score_tuesday_tour_disabled') === 'true';
        if (shouldShow && !isTuesdayTourDisabled) {
            setIsTuesdayModalOpen(true);
            localStorage.setItem('driver_score_tuesday_modal_last_shown', JSON.stringify({
                date: todayDateStr,
                block: currentBlock
            }));
        }
    }, [isLoading, userProfile, isPremiumOrVip]);

    const formatValue = (value: number | string) => {
        if (isPrivacyMode) return '****';
        if (typeof value === 'number') return `R$ ${value.toFixed(2)}`;
        return value;
    };

    // Scheduler: Alarm & Rhino Service
    useEffect(() => {
        const runSchedulers = async () => {
            const now = new Date();
            const day = now.getDay(); // 0-6 (Sun-Sat), 2 is Tuesday
            const hours = now.getHours();
            const minutes = now.getMinutes();

            // 1. Alarm Check & Schedule Notification
            const isAlarmEnabled = localStorage.getItem('driver_score_alarm_enabled') === 'true';

            // Get user's current tier from rhino_score
            const scoreToUse = userProfile?.rhino_score ?? userProfile?.xp_points ?? 0;
            const currentTier = scoreToUse >= 95 ? 'Ouro' : scoreToUse >= 80 ? 'Prata' : 'Bronze';

            // Tier-based Rhino schedule:
            //   Ouro   → Monday  (day=1) at 16:00
            //   Prata  → Tuesday (day=2) at 12:00
            //   Bronze → Tuesday (day=2) at 16:00
            let targetDay: number, targetHour: number;
            if (currentTier === 'Ouro') {
                targetDay = 1; targetHour = 16;
            } else if (currentTier === 'Prata') {
                targetDay = 2; targetHour = 12;
            } else {
                targetDay = 2; targetHour = 16;
            }
            const targetMin = 0;

            const isAlarmTime = day === targetDay && hours === targetHour && minutes === targetMin;

            if (isAlarmEnabled && isAlarmTime) {
                // Pick track: use saved preference or random
                const savedSoundId = localStorage.getItem('driver_score_alarm_sound');
                let trackToPlay;
                if (savedSoundId && savedSoundId !== 'random') {
                    trackToPlay = YOUTUBE_TRACKS.find(t => t.id === savedSoundId) || YOUTUBE_TRACKS[Math.floor(Math.random() * YOUTUBE_TRACKS.length)];
                } else {
                    trackToPlay = YOUTUBE_TRACKS[Math.floor(Math.random() * YOUTUBE_TRACKS.length)];
                }

                // Fire the in-app overlay (Alarm logic)
                if (!isAlarmRinging) {
                    setActiveAlarmTrack(trackToPlay);
                    setHasInteractedWithAlarm(false);
                    setIsAlarmRinging(true);
                }

                // Fire native push notification safely
                if ('Notification' in window && Notification.permission === 'granted') {
                    try {
                        const showNotification = async () => {
                            if ('serviceWorker' in navigator) {
                                const registration = await navigator.serviceWorker.ready;
                                registration.showNotification("Hora de acordar, Chofer!", {
                                    body: `Seu despertador Driver Score está tocando! Música: ${trackToPlay?.label || 'DSAudio'}`,
                                    icon: "/logo192.png",
                                    badge: "/logo192.png",
                                    vibrate: [200, 100, 200],
                                    tag: 'driver-score-alarm',
                                    requireInteraction: true
                                } as any);
                            } else {
                                new Notification("Hora de acordar, Chofer!", {
                                    body: `Seu despertador Driver Score está tocando! Música: ${trackToPlay?.label || 'DSAudio'}`,
                                    icon: "/logo192.png",
                                    requireInteraction: true
                                });
                            }
                        };
                        showNotification();
                    } catch (err) {
                        console.error('Error triggering alarm notification:', err);
                    }
                }
            }

            // 2. Rhino Email Check (Premium/VIP ONLY)
            if (session?.user?.id && isPremiumOrVip) {
                const isGoogle = session.app_metadata?.provider === 'google' ||
                    session.user?.app_metadata?.provider === 'google' ||
                    (session as any).user?.identities?.some((id: any) => id.provider === 'google');

                try {
                    const providerToken = getProviderToken();
                    await RhinoService.checkAndProcess(session.user.id, !!isGoogle, providerToken);
                } catch (error: any) {
                    if (error.message === 'NON_GOOGLE_ACCOUNT') {
                        const lastShown = localStorage.getItem('rhino_error_shown');
                        const today = new Date().toDateString();
                        if (lastShown !== today) {
                            setShowRhinoError(true);
                        }
                    }
                }
            } else if (session?.user?.id && !isPremiumOrVip) {
                // Optimization: If not premium, we don't even check, but we could log something if needed.
            }
        };

        const interval = setInterval(runSchedulers, 60000); // Check every minute
        runSchedulers(); // Check immediately on mount

        return () => clearInterval(interval);
    }, [isAlarmRinging, session]);

    const fetchUserData = useCallback(async (userId: string) => {
        setIsLoading(true);
        try {
            // Fetch or Initialize Profile FIRST to get generation
            let currentProfile = null;
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .maybeSingle();

            if (profileError && profileError.code !== 'PGRST116') {
                console.error('Error fetching profile:', profileError);
            }

            if (profileData) {
                setUserProfile(profileData);
                currentProfile = profileData;
            } else {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const newProfile: UserProfile = {
                        id: user.id,
                        nickname: user.user_metadata?.full_name?.split(' ')[0] || 'Novo',
                        xp_points: 0,
                        tier: 'Bronze',
                        created_at: new Date().toISOString(),
                        email: user.email,
                        generation: 'OLD',
                        generation_selected: false
                    };
                    const { error: insertError } = await supabase
                        .from('profiles')
                        .insert(newProfile);
                    if (insertError) console.error('Error creating profile:', insertError);
                    setUserProfile(newProfile);
                    currentProfile = newProfile;
                }
            }

            const userGeneration = currentProfile?.generation || 'OLD';

            // Fetch from 'user_shifts' as per screenshot schema
            const { data: shiftsData, error: shiftsError } = await supabase
                .from('user_shifts')
                .select('*')
                .eq('user_id', userId);

            if (shiftsError) console.error('Error fetching shifts:', shiftsError);

            if (shiftsData) {
                const cleanShifts = shiftsData.map(item => {
                    try {
                        return fromDbShift(item, userGeneration);
                    } catch (e) {
                        console.error("Error parsing shift:", item, e);
                        return null;
                    }
                }).filter(Boolean) as Shift[];

                setShifts(cleanShifts);

                // Auto-create garage checkins for today's planned shifts
                const todayStr = new Date().toISOString().split('T')[0];
                const todaysShifts = cleanShifts.filter(s => 
                    s.date === todayStr && s.type === 'WORK' && (s.plannedStartTime || s.startTime)
                );

                if (todaysShifts.length > 0) {
                    for (const shift of todaysShifts) {
                        const schedStart = shift.plannedStartTime || shift.startTime;
                        const plate = shift.vehiclePlate || 'SEM PLACA';
                        await supabase.from('garage_checkins').upsert({
                            user_id: userId,
                            shift_date: todayStr,
                            scheduled_start: schedStart,
                            vehicle_plate: plate,
                        }, { onConflict: 'user_id,shift_date,scheduled_start' });
                    }
                }

                // Fetch garage checkins for yesterday and today (to support overnight shifts)
                const yesterdayObj = new Date(todayStr + 'T12:00:00'); // secure timezone mid-day
                yesterdayObj.setDate(yesterdayObj.getDate() - 1);
                const yesterdayStr = yesterdayObj.toISOString().split('T')[0];

                const { data: gcData } = await supabase
                    .from('garage_checkins')
                    .select('*')
                    .eq('user_id', userId)
                    .in('shift_date', [yesterdayStr, todayStr]);

                if (gcData) {
                    // Only show pending/active check-ins (exclude COMPLETED so the next planned shift shows immediately)
                    const activeCheckins = gcData.filter(c => c.status !== 'COMPLETED');
                    setGarageCheckins(activeCheckins);
                }
            }

        } catch (error) {
            console.error('Error in fetchUserData:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Handle Mercado Pago Premium Success Redirect and query param routing
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('v') === 'premium_success') {
            if (session?.user?.id) {
                fetchUserData(session.user.id);
                // Clear URL params without reload immediately before the blocking alert
                window.history.replaceState({}, document.title, window.location.pathname);
                alert("Parabéns! Agora você é Driver Score Premium 🏆\nSeu selo já está ativo e seus anúncios duram o mês todo!");
            }
        }

        const viewParam = urlParams.get('view');
        if (viewParam === 'desempenho') {
            setView(ViewMode.PERFORMANCE);
            setViewHistory([ViewMode.DASHBOARD, ViewMode.PERFORMANCE]);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [session, fetchUserData]);

    const startTour = () => {
        setTourStep(1);
        setView(ViewMode.DASHBOARD);
    };

    const finishTour = () => {
        setTourStep(0);
        localStorage.setItem('driver_score_tour_completed', 'true');
        setShowPwaTip(true); // Show PWA tip at the end of the tour
    };

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            if (session) {
                storeProviderToken(session);
                fetchUserData(session.user.id);
            }
            setIsCheckingSession(false);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setIsCheckingSession(false);
            if (session) {
                storeProviderToken(session);
                fetchUserData(session.user.id);
                const hash = window.location.hash;
                if (hash && hash.includes('type=recovery')) {
                    setIsRecoveryMode(true);
                }
            } else {
                setShifts([]);
                setUserProfile(null);
                setHasAccess(false);
                localStorage.removeItem('google_provider_token');
                localStorage.removeItem('google_provider_token_expires_at');
            }
        });

        return () => subscription.unsubscribe();
    }, [fetchUserData]);

    // Handle back button navigation (mobile hardware back and browser back)
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            event.preventDefault();
            if (viewHistory.length > 1) {
                const newHistory = viewHistory.slice(0, -1);
                setViewHistory(newHistory);
                setView(newHistory[newHistory.length - 1]);
            }
        };

        // Listen for browser/app back button
        window.addEventListener('popstate', handlePopState);
        
        // Handle Android back button
        const handleHardwareBack = () => {
            if (viewHistory.length > 1) {
                const newHistory = viewHistory.slice(0, -1);
                setViewHistory(newHistory);
                setView(newHistory[newHistory.length - 1]);
            }
        };

        // Register Android hardware back button listener if available
        if (typeof (window as any).AndroidBackHandler !== 'undefined') {
            (window as any).AndroidBackHandler = handleHardwareBack;
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [viewHistory]);

    // Handle Referral Tracking on Login (specifically for OAuth where signUp metadata isn't easily passed)
    useEffect(() => {
        if (session && session.user) {
            const storedRef = localStorage.getItem('driver_score_referred_by');
            if (storedRef && !session.user.user_metadata?.referred_by) {
                console.log("🚀 Aplicando código de indicação...");
                supabase.auth.updateUser({
                    data: { referred_by: storedRef }
                }).then(() => {
                    localStorage.removeItem('driver_score_referred_by');
                });
            } else if (session.user.user_metadata?.referred_by) {
                // Ensure cleanup if already saved
                localStorage.removeItem('driver_score_referred_by');
            }
        }
    }, [session]);

    // Handle Pending Elite Upgrade from Landing Page
    useEffect(() => {
        if (session && userProfile && !isPremiumOrVip) {
            const pendingUpgrade = localStorage.getItem('pendingEliteUpgrade');
            if (pendingUpgrade === 'true') {
                localStorage.removeItem('pendingEliteUpgrade');
                // Small delay to ensure the dashboard has fully rendered and the modal doesn't glitch
                setTimeout(() => {
                    handleRequirePremium('Ativação do Modo Elite');
                }, 800);
            }
        }
    }, [session, userProfile, isPremiumOrVip]);

    const handleShiftSubmit = async (input: Shift | Shift[], navigateToDashboard = true) => {
        if (!session) return;
        const user = session.user;

        const itemsToSave = Array.isArray(input) ? input : [input];
        const newItemsIds = new Set<string>();
        itemsToSave.forEach(item => {
            const exists = shifts.some(existing => existing.id === item.id);
            if (!exists) {
                newItemsIds.add(item.id);
            }
        });



        try {
            const currentGen = userProfile?.generation || 'OLD';
            const dbItems = itemsToSave.map(item => {
                // Preserve existing generation or use current profile generation for new shifts
                const itemWithGen = item.generation ? item : { ...item, generation: currentGen as 'OLD' | 'NEW' };
                const itemWithUser = { ...itemWithGen, user_id: user.id };
                return toDbShift(itemWithUser);
            });

            // Save to 'user_shifts' with fallback if logout_telegram_time column is not created in DB yet
            let response = await supabase.from('user_shifts').upsert(dbItems).select();

            if (response.error && (response.error.code === 'PGRST204' || response.error.message?.includes('logout_telegram_time'))) {
                console.warn("DB migration for logout_telegram_time not applied yet. Retrying without it...");
                const cleanedDbItems = dbItems.map(item => {
                    const newItem = { ...item };
                    delete (newItem as any).logout_telegram_time;
                    return newItem;
                });
                response = await supabase.from('user_shifts').upsert(cleanedDbItems).select();
            }

            const { data, error } = response;

            if (error) throw error;

            if (data) {
                const savedShifts = data.map(item => fromDbShift(item, userProfile?.generation || 'OLD'));
                const currentShifts = [...shifts];

                savedShifts.forEach(savedItem => {
                    const idx = currentShifts.findIndex(s => s.id === savedItem.id);
                    if (idx >= 0) {
                        currentShifts[idx] = savedItem;
                    } else {
                        currentShifts.push(savedItem);
                    }

                    if (newItemsIds.has(savedItem.id)) {
                        processShiftGamification(user.id, savedItem);
                    }
                });
                setShifts(currentShifts);

                if (newItemsIds.size > 0) {
                    fetchUserData(user.id);



                    // --- Referral Zere seu Ano Logic ---
                    if (isPremiumOrVip) {
                        const now = new Date();
                        if (now.getDay() === 0) { // Sunday
                            const hasProfitable = itemsToSave.some(s => (s.totalEarnings + s.tips + s.appTips) > 0);
                            if (hasProfitable) {
                                const lastShown = localStorage.getItem('driver_score_zere_ano_last_shown');
                                const todayStr = now.toISOString().split('T')[0];
                                if (lastShown !== todayStr) {
                                    setIsZereSeuAnoModalOpen(true);
                                    localStorage.setItem('driver_score_zere_ano_last_shown', todayStr);
                                }
                            }
                        }
                    }
                }
            }

            if (navigateToDashboard) {
                setFormInitialData(null);
                changeView(ViewMode.DASHBOARD);
            }
        } catch (err) {
            console.error("Error saving shifts:", err);
            alert("Erro ao salvar turno(s). Tente novamente.");
        }
    };

    const handleDeleteShift = async (id: string) => {
        try {
            const { error } = await supabase.from('user_shifts').delete().eq('id', id);
            if (error) throw error;
            setShifts(shifts.filter(s => s.id !== id));
        } catch (err) {
            console.error(err);
            alert("Erro ao excluir.");
        }
    };

    const handleEditShift = (shift: Shift) => {
        setFormInitialData({
            id: shift.id,
            type: shift.type,
            date: shift.date,
            startTime: shift.startTime,
            endTime: shift.endTime,
            isHoliday: shift.isHoliday,
            hasBilingual: !!shift.bilingualStart,
            bilingualStart: shift.bilingualStart || '',
            bilingualEnd: shift.bilingualEnd || '',
            appTips: (shift.appTips || 0).toString(),
            tips: (shift.tips || 0).toString(),
            unpaidBreakMinutes: (shift.unpaidBreakMinutes || 0).toString(),
            expenses: shift.expenses || [],
            extraEarnings: shift.extraEarnings || [],
            vehiclePlate: shift.vehiclePlate || '',
            carReadyOnTime: shift.carReadyOnTime ?? true,
            car_arrival_time: shift.carArrivalTime || null,
            login_app_time: shift.loginAppTime || null,
            login_telegram_time: shift.loginTelegramTime || null
        });
        setView(ViewMode.ADD_SHIFT);
    };

    const handleDuplicateShift = (shift: Shift) => {
        setFormInitialData({
            type: shift.type,
            date: shift.date,
            startTime: shift.startTime,
            endTime: shift.endTime,
            isHoliday: shift.isHoliday,
            hasBilingual: !!shift.bilingualStart,
            bilingualStart: shift.bilingualStart || '',
            bilingualEnd: shift.bilingualEnd || '',
            appTips: (shift.appTips || 0).toString(),
            tips: (shift.tips || 0).toString(),
            expenses: shift.expenses || [],
            extraEarnings: shift.extraEarnings || [],
            vehiclePlate: shift.vehiclePlate || '',
            carReadyOnTime: shift.carReadyOnTime ?? true,
            car_arrival_time: shift.carArrivalTime || null,
            login_app_time: shift.loginAppTime || null,
            login_telegram_time: shift.loginTelegramTime || null
        });
        setView(ViewMode.ADD_SHIFT);
    };

    const NavItem = ({ mode, icon: Icon, label, isPremium }: { mode: ViewMode, icon: any, label: string, isPremium?: boolean }) => (
        <button
            onClick={() => {
                if (mode === ViewMode.PROFILE) setViewingProfileId(null);
                changeView(mode);
            }}
            className={`flex items-center justify-between p-3 rounded-xl w-full transition-all ${view === mode ? 'bg-orange-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
        >
            <div className="flex items-center gap-3">
                <Icon className="w-5 h-5" />
                <span className="font-medium">{label}</span>
            </div>
            {isPremium && !isPremiumOrVip && (
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500">
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
            )}
        </button>
    );


    // Garage Agent Route — renders standalone, no login needed
    if (isGarageRoute) {
        return (
            <>
                <DottedSurface />
                <GarageAgentView />
            </>
        );
    }

    if (isCheckingSession) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
        );
    }

    if (!session) {
        return (
            <>
                <DottedSurface />
                <InstallPWA />
                <Auth />
            </>
        );
    }

    if (isRecoveryMode) {
        return (
            <>
                <DottedSurface />
                <PasswordUpdate onSuccess={() => setIsRecoveryMode(false)} />
            </>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 text-slate-100 font-sans selection:bg-orange-500/30">
            <DottedSurface />
            <UpdateBanner />
            {showChangelogModal && (
                <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-amber-500/30 w-full max-w-md rounded-2xl p-6 shadow-2xl flex flex-col max-h-[90vh]">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-black text-white flex items-center gap-2">
                                <span className="p-2 bg-amber-500/20 text-amber-500 rounded-lg">🚀</span>
                                Notas de Atualização
                            </h2>
                            <button onClick={() => setShowChangelogModal(false)} className="text-slate-400 hover:text-white transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="overflow-y-auto pr-2 space-y-4 flex-1 custom-scrollbar">
                            {/* v1.14.1 — NOVO */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-orange-500/30 border-l-4 border-l-orange-500">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.14.1 — Preço de Assinatura Unificado (R$ 10,90) 💳🎯</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Assinatura Corrigida:</strong> Corrigido o valor da assinatura no Mercado Pago para cobrar exatamente o preço unificado de R$ 10,90/mês para todos os usuários (estava cobrando R$ 16,90 após o prazo promocional).</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Interface de Acesso:</strong> Ajustado o preço exibido nos componentes de controle de acesso para refletir permanentemente o valor unificado de R$ 10,90/mês.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.14.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-purple-500">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.14.0 — Acessibilidade WCAG 2.2 & SEO Otimizado 🔍🛡️</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Melhorias de Contraste:</strong> Ajustamos as cores dos subtextos no Light Mode e elementos do Tema Copa do Mundo para garantir máxima legibilidade e conformidade total com o padrão WCAG 2.2 AA.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">SEO & Social Sharing:</strong> Adicionamos metatags OpenGraph e Twitter Cards de alta qualidade para carregar previews visuais elegantes ao compartilhar o link do app.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Dados Estruturados:</strong> Inserção de scripts JSON-LD de marcação do Schema.org para identificar o app nos mecanismos do Google.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.13.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-purple-500">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.13.0 — Sistema Multitemas & Copa do Mundo 🎨⚽</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Claro & Escuro:</strong> Adicionado suporte a Light Mode de alta legibilidade, mantendo o padrão Dark Mode.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Tema Copa do Mundo:</strong> Um visual imersivo verde/amarelo com textura de gramado, confetes nas cores do Brasil e sons de apito de juiz ao bater metas.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Atalhos de Temas:</strong> Seletor rápido em formato de emoji ciclável (🌙, ☀️, ⚽) inserido nos cabeçalhos e painel de perfil.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Tour de Novidades:</strong> Um mini modal de 3 passos que ensina a usar as novas cores e recursos de som/confete ao abrir o app.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.12.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-purple-500">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.12.0 — Turnos Programados & Ganhos Brutos 📊⏰</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Turnos Programados:</strong> Renomeação da aba de cadastro e simplificação do fluxo. Removemos a tolerância de 5 min do veículo, que agora será sinalizada na tela inicial de garagem.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Lucro Real Mensal:</strong> O demonstrativo de lucro do mês no dashboard agora reflete apenas o acumulado do mês corrente, zerando a cada novo período.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Saldo Bruto Acumulado:</strong> A exibição do Saldo Disponível agora reflete seu faturamento histórico bruto total sem deduções de despesas.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Adiantamento Consolidado:</strong> Antecipações via Plific agora geram um único turno consolidado no histórico com layout de transação detalhado (líquido e taxa de 4%).</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Sincronização Gmail:</strong> Painel de Desempenho atualizado para mostrar o status real do token Gmail com alertas de expiração e re-sincronização automática.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Despertador Push:</strong> O alarme agora dispara notificações locais do navegador mesmo se minimizado (PWA). Solicitando permissões ao ser ativado.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.11.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-purple-500">
                                <h3 className="font-bold text-purple-400 text-sm mb-3">v1.11.0 — A Grande Jornada & Stories Livres 🌌🚀</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Jornada de 22 Níveis:</strong> Escalada épica de Ferro até Singularidade (1 Milhão de pontos) com designs personalizados e molduras pulsantes.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Stories Gratuitos:</strong> Anuncie e poste stories no Marketplace de forma 100% gratuita, ilimitada e sem travas de planos.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Limpeza e Foco:</strong> Removemos os recursos legados (Tutor de Inglês e Faturamento) para focar 100% no Driver Score.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Progresso Fiel:</strong> Barra de progresso relativo atualizada para todos os tiers, exibindo o status preciso em relação às faixas de XP.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.10.1 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-purple-500">
                                <h3 className="font-bold text-purple-400 text-sm mb-3">v1.10.1 — Gerador de Avatar IA 🤖✨</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Imagen 3:</strong> Crie fotos de perfil exclusivas com a inteligência artificial do Google.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Estilos VIP:</strong> 7 estilos artísticos (GTA, Pixar, Cyberpunk e mais) para membros Premium.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Segurança:</strong> Sistema de cooldown de 15 dias para garantir a exclusividade.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.9.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-amber-500 mb-4">
                                <h3 className="font-bold text-amber-400 text-sm mb-3">v1.9.0 — Navegação, Stories e Rhino 💫</h3>
                                <ul className="space-y-3">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-amber-500" /></div>
                                        <div><strong className="text-white">Botão Voltar Inteligente:</strong> O botão voltar do celular agora retorna para a página anterior onde você estava, não mais para o início.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-amber-500" /></div>
                                        <div><strong className="text-white">Geração por Turno:</strong> A geração (Antiga/Nova) agora é salva junto com cada turno. O histórico respeita a métrica selecionada no momento da inserção.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-amber-500" /></div>
                                        <div><strong className="text-white">Placa Auto-Formatada:</strong> Ao inserir a placa do veículo, o formato é ajustado automaticamente (ABC1234 → ABC 1234).</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-amber-500" /></div>
                                        <div><strong className="text-white">Login Google Ativo:</strong> O login com Google está funcionando normalmente. Campo de placa removido do cadastro.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-amber-500" /></div>
                                        <div><strong className="text-white">Stories com Áudio:</strong> Videos agora tocam com som automaticamente. Toque na tela para mostrar/ocultar textos ou entrar em fullscreen.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-amber-500" /></div>
                                        <div><strong className="text-white">Desempenho:</strong> Integração com email da Rhino agora mostra seu tier real (Ouro/Prata/Bronze) e eventos reais do sistema.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.7.0 — NOVO */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-emerald-500 mb-4">
                                <h3 className="font-bold text-emerald-400 text-sm mb-3">v1.7.0 — Auditoria Avançada de Turnos 🔍</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Placa do Veículo Obrigatória:</strong> Agora cada turno registra a placa do veículo utilizado, visível no card do histórico para rastreabilidade total.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Logoff Antecipado (x2):</strong> Saiu antes do fim planejado? O sistema calcula automaticamente a multa de 2x sobre os minutos faltantes.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Verificação do Carro:</strong> Se o veículo não estava disponível, marque no checkbox e o atraso será isento automaticamente — conforme política Rhino.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Espelho Inteligente:</strong> O Espelho de Horas agora calcula automaticamente Login com Atraso, Logoff Antecipado e Pausa diretamente dos dados dos turnos.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Bônus 10% Automático:</strong> O bônus é automaticamente removido se houver 2 ou mais atrasos acima de 10 min no mês.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Planejado vs Realizado:</strong> O card do histórico agora exibe o horário planejado original quando houver diferença com o horário real.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.6.1 — NOVO */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-orange-500 mb-4">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.6.1 — Auditoria de Ganhos & Espelho Dinâmico 💰</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Trava de Perfil:</strong> Escolha obrigatória entre "Geração Antiga" e "Geração Nova" antes de inserir turnos para garantir a auditoria exata.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Espelho de RH Atualizado:</strong> O seu extrato gerado agora reflete com exatidão a matemática da sua geração.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Adicional Bilíngue:</strong> Novo campo de Adicional Bilíngue no seu Espelho, que já soma ao Total da sua NF.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.6.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-blue-500 mb-4">
                                <h3 className="font-bold text-blue-400 text-sm mb-3">v1.6.0 — Auditoria Inteligente de Escalas ⏱️</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Escalas Planejadas:</strong> Agora você pode inserir sua escala da semana e o Driver Score irá comparar com o que você realmente trabalhou.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Calculadora de Atraso (x2):</strong> Chegou depois de 5 minutos do horário planejado? O sistema aplica a penalidade multiplicando o desconto por 2 automaticamente.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Bônus de Pausa Salva:</strong> Reduziu o horário de almoço? Veja exatamente o valor adicional (R$) ganho por ter salvado o descanso.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Notificações Inteligentes:</strong> Banner te avisa se esqueceu de lançar a escala da semana.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.5.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-purple-500 mb-4">
                                <h3 className="font-bold text-purple-400 text-sm mb-3">v1.5.0 — Stories Interativos & Ranking Inteligente 🎬</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Ranking Auto-Reset:</strong> Semanal e mensal zeram automaticamente. Geral permanece.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Stories Auto-Play:</strong> Avança automaticamente entre stories (7s imagens, fim do vídeo).</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Reações com Emoji:</strong> Curta stories com 👍❤️😂🔥👏😍 direto no app.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Quem Visualizou:</strong> Todos veem quem viu o story. Toque no avatar para ver o perfil.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Links Sociais:</strong> Adicione link do YouTube, Instagram ou TikTok no story. YouTube roda direto no app.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Upload de Vídeo:</strong> Correção do envio de vídeos no Marketplace.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.4.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-green-500 mb-4">
                                <h3 className="font-bold text-green-400 text-sm mb-3">v1.4.0 — Ranking Social & Fundadores 👑</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Top 100 Ranking:</strong> Ranking expandido para os 100 melhores motoristas.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Rastrear Amigos:</strong> Busque e siga motoristas para acompanhar no ranking.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Badge Fundador:</strong> Selo exclusivo para membros fundadores.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Cancelar Assinatura:</strong> Cancele direto pelo app sem burocracia.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Avatar Melhorado:</strong> Novo sistema de avatar com upload e visualização.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.3.4 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-blue-500 mb-4">
                                <h3 className="font-bold text-blue-400 text-sm mb-3">v1.3.4 — Expansão e Tutor de Inteligência 🚀</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">English Tutor IA (BETA VIP):</strong> Professor particular com IA, voz e conversação em tempo real.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Evolução de Marca:</strong> Rebrand completo para Driver Score em todos os painéis.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.3.3 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-orange-500">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.3.3 — Refinamentos e Correções 🛠️</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Edição Inteligente:</strong> Formulário mantém a data real do turno ao editar.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Marketplace:</strong> Correção de bugs ao deletar anúncios vendidos.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Ranking Consistente:</strong> Correção de erro 'Not Found' ao filtrar pódio.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Histórico:</strong> Tooltips explicando status das horas de descanso.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.3.2 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-emerald-500">
                                <h3 className="font-bold text-emerald-400 text-sm mb-3">v1.3.2 — Privacidade e Ranking Interativo 🛡️</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Ranking Conectado:</strong> Clique em qualquer motorista para ver perfil e elo.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Estatísticas Ocultas:</strong> Ocupe o ranking anonimamente.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Avatar em Sigilo:</strong> Esconda sua foto de outras contas.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.3.1 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-indigo-500">
                                <h3 className="font-bold text-indigo-400 text-sm mb-3">v1.3.1 — Mapa UX Mobile 🗺️</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">GPS Instantâneo:</strong> Detecta localização e preenche formulário.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Formatador de Placa:</strong> Maiúsculas e espaço automático.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Endereço Limpo:</strong> Remove CEP, números e "Brasil" automaticamente.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Crash Zero:</strong> Erro "Illegal Constructor" no Android eliminado.</div>
                                    </li>
                                </ul>
                            </div>

                            {/* v1.3.0 */}
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 border-l-4 border-l-amber-500">
                                <h3 className="font-bold text-orange-400 text-sm mb-3">v1.3.0 — Mapa & Estabilidade</h3>
                                <ul className="space-y-4">
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Mapa & Frota:</strong> Banheiros, pontos zero zona azul e abastecimento com IA.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">App Blindado:</strong> Fim dos travamentos no Android.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Redes Sociais:</strong> Instagram, YouTube, TikTok e mais no perfil.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Login Simplificado:</strong> Acesso direto sem landing page.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Marketplace Grátis:</strong> Todos os anúncios grátis por 3 meses.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Stories Tela Cheia:</strong> Fotos e vídeos sem corte.</div>
                                    </li>
                                    <li className="flex gap-3 text-sm text-slate-300">
                                        <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-green-500" /></div>
                                        <div><strong className="text-white">Controle de Versão:</strong> Badge dinâmico com selo Elite.</div>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700/30 border-l-4 border-l-slate-600">
                                <h3 className="font-bold text-slate-500 text-sm mb-3">v1.2.0 - O Pulo do Gato</h3>
                                <ul className="space-y-3">
                                    <li className="flex gap-3 text-xs text-slate-500">
                                        <div className="mt-0.5"><CheckCircle2 className="w-3 h-3 text-slate-600" /></div>
                                        <div>Calculadora de Notas com conferência automática (DS vs Plific).</div>
                                    </li>
                                    <li className="flex gap-3 text-xs text-slate-500">
                                        <div className="mt-0.5"><CheckCircle2 className="w-3 h-3 text-slate-600" /></div>
                                        <div>Check de Município e Central SOS com botões diretos.</div>
                                    </li>
                                    <li className="flex gap-3 text-xs text-slate-500">
                                        <div className="mt-0.5"><CheckCircle2 className="w-3 h-3 text-slate-600" /></div>
                                        <div>Limpeza de cache automática para versões novas.</div>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-800">
                            <button 
                                onClick={() => setShowChangelogModal(false)}
                                className="w-full bg-slate-800 hover:bg-slate-700 text-amber-500 font-bold py-3 rounded-xl transition-all border border-slate-700 active:scale-95"
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <InstallPWA />
            <NewsBanner
                id="beta-launch"
                expiresAt="2025-12-31"
                title="Novidade!"
                message="O Driver Score agora tem busca de locais com IA e gamificação!"
                actionLabel="Ver Perfil"
                onAction={() => changeView(ViewMode.PROFILE)}
            />

            {/* Mobile Header */}
            <header className="md:hidden bg-slate-800/80 backdrop-blur-md border-b border-slate-700 p-4 sticky top-0 z-30 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    {viewHistory.length > 1 ? (
                        <button 
                            onClick={() => {
                                const newHistory = viewHistory.slice(0, -1);
                                setViewHistory(newHistory);
                                setView(newHistory[newHistory.length - 1]);
                            }}
                            className="p-2 text-slate-300 hover:bg-slate-700 rounded-lg animate-fade-in"
                            title="Voltar"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    ) : (
                        <div className="w-10" />
                    )}
                    <h1 className="font-bold text-lg text-white">Driver Score</h1>
                    <button 
                        onClick={() => setShowChangelogModal(true)}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition-colors border shadow-md ${isPremiumOrVip ? 'bg-amber-500/20 text-amber-500 border-amber-500/30 hover:bg-amber-500/30 shadow-amber-900/20' : 'bg-slate-700/50 text-slate-300 border-slate-600 hover:bg-slate-700'}`}
                    >
                        v{APP_VERSION} {isPremiumOrVip && 'Elite'}
                    </button>
                </div>
                <div className="flex items-center gap-1">
                    <button 
                        onClick={cycleTheme}
                        className="p-2 text-slate-300 hover:bg-slate-700 rounded-lg text-base flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                        title="Mudar Tema"
                    >
                        {getThemeIcon()}
                    </button>
                    <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-slate-300 hover:bg-slate-700 rounded-lg">
                        <Menu className="w-6 h-6" />
                    </button>
                </div>
            </header>

            {isSidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setIsSidebarOpen(false)} />}

            <div className="flex max-w-7xl mx-auto min-h-screen">
                <aside id="tour-sidebar" className={`
                fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 border-r border-slate-700 transform transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:block flex flex-col
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
             `}>
                    <div className="flex flex-col h-full overflow-y-auto px-6 pt-6 pb-6">
                    <div className="flex justify-between items-center mb-8">
                        <div className="flex items-center gap-2">
                            <div className="bg-gradient-to-br from-orange-500 to-orange-700 p-2 rounded-xl shadow-lg shadow-orange-900/20">
                            </div>
                            <div>
                                <h1 className="font-bold text-xl text-white tracking-tight">Driver Score</h1>
                                <button 
                                    onClick={() => setShowChangelogModal(true)}
                                    className={`mt-1 text-[10px] px-2 py-0.5 rounded-full font-bold transition-colors border shadow-md ${isPremiumOrVip ? 'bg-amber-500/20 text-amber-500 border-amber-500/30 hover:bg-amber-500/30 shadow-amber-900/20' : 'bg-slate-700/50 text-slate-300 border-slate-600 hover:bg-slate-700'}`}
                                >
                                    v{APP_VERSION} {isPremiumOrVip && 'Elite'}
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button 
                                onClick={cycleTheme}
                                className="p-2 text-slate-300 hover:bg-slate-700 rounded-lg text-base flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                                title="Mudar Tema"
                            >
                                {getThemeIcon()}
                            </button>
                            <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-slate-400"><X className="w-6 h-6" /></button>
                        </div>
                    </div>

                    <nav className="space-y-2">
                        <NavItem mode={ViewMode.DASHBOARD} icon={LayoutDashboard} label="Dashboard" />
                        <NavItem mode={ViewMode.FINANCE} icon={Wallet} label="Financeiro" />
                        <div id="tour-ranking">
                            <NavItem mode={ViewMode.RANKING} icon={Trophy} label="Ranking" />
                        </div>
                        {/* Hide Desempenho temporarily as requested */}
                        {/* <NavItem mode={ViewMode.PERFORMANCE} icon={BarChart2} label="Desempenho" /> */}
                        <NavItem mode={ViewMode.ADD_SHIFT} icon={PlusCircle} label="Turnos Programados" />
                        <NavItem mode={ViewMode.HISTORY} icon={History} label="Histórico" />
                        <NavItem mode={ViewMode.FLEET} icon={Car} label="Frota & Mapa" />
                        <NavItem mode={ViewMode.PARTNERS} icon={Crown} label="Parceiros" />
                        <NavItem mode={ViewMode.PROFILE} icon={User} label="Meu Perfil" />
                        <NavItem mode={ViewMode.SUPPORT_CENTER} icon={Headset} label="Central de Apoio" />
                        <NavItem mode={ViewMode.FEEDBACK} icon={MessageSquare} label="Sugestões" />
                    </nav>

                    <div className="mt-auto pt-6">
                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 mb-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 rounded-full bg-orange-600 flex items-center justify-center font-bold text-white text-xs">
                                    {(userProfile?.nickname || session.user?.email || 'Mot').substring(0, 2).toUpperCase()}
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-sm font-bold text-white truncate">{userProfile?.nickname || 'Motorista'}</p>
                                    <p className="text-xs text-slate-500 truncate">{session.user?.email || ''}</p>
                                </div>
                            </div>
                            <button
                                onClick={logoutUser}
                                className="w-full flex items-center justify-center gap-2 text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-900/20 py-2 rounded-lg transition-colors cursor-pointer"
                            >
                                <LogOut className="w-3 h-3" /> Sair da Conta
                            </button>
                        </div>
                    </div>
                    </div>
                </aside>

                <main className="flex-1 p-4 md:p-8 w-full max-w-full overflow-hidden pb-24 md:pb-8 flex flex-col">
                    {/* Expiring Subscription Banner */}
                    {isExpiringSoon && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 flex items-center justify-between gap-4 animate-pulse-gentle shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-500/20 rounded-lg">
                                    <ShieldCheck className="text-red-400 w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-red-400 font-bold text-sm">⚠️ Sua proteção está expirando!</h4>
                                    <p className="text-red-300/80 text-xs mt-0.5">Renove agora para não perder o controle do bônus mensal e a auditoria centesimal. Faltam {daysUntilExpiration} dia(s).</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => changeView(ViewMode.PROFILE)}
                                className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap shadow-lg shadow-red-900/20"
                            >
                                Renovar
                            </button>
                        </div>
                    )}
                    {/* Trial Free Warning Banner — shown to non-premium users within their 7-day trial */}
                    {!isPremiumOrVip && isWithinTrialPeriod && trialDaysRemaining !== null && (
                        <div className="relative overflow-hidden rounded-2xl mb-4 shrink-0 animate-pulse-gentle" style={{background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 40%, #1d4ed8 100%)'}}>
                            {/* Shimmer overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 animate-[shimmer_2s_infinite]" style={{backgroundSize: '200% 100%'}} />
                            <div className="relative p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <div className="flex items-center gap-3 flex-1">
                                    <div className="p-2.5 bg-white/20 rounded-xl shrink-0 backdrop-blur-sm">
                                        <span className="text-xl">⭐</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-black text-sm md:text-base leading-tight">
                                            Período de Teste Grátis: <span className="bg-white/20 px-2 py-0.5 rounded-lg font-black">{trialDaysRemaining} dia{trialDaysRemaining !== 1 ? 's' : ''} restante{trialDaysRemaining !== 1 ? 's' : ''}</span>
                                        </h4>
                                        <p className="text-blue-100 text-xs md:text-sm mt-0.5 leading-snug">
                                            Aproveite o acesso completo ao Driver Score! Assine o plano Premium para garantir o seu acesso ilimitado após o período de teste.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRequirePremium('Assinatura Premium')}
                                    className="w-full sm:w-auto shrink-0 bg-white hover:bg-blue-50 active:scale-95 text-blue-600 font-black text-sm px-5 py-2.5 rounded-xl shadow-lg transition-all whitespace-nowrap"
                                >
                                    Seja Premium 🚀
                                </button>
                            </div>
                        </div>
                    )}
                    {isLoading && shifts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                            <Loader2 className="w-8 h-8 animate-spin mb-2 text-orange-500" />
                            <p>Carregando seus dados...</p>
                        </div>
                    ) : (!isPremiumOrVip && !isWithinTrialPeriod && isAfterBlockDeadline) ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 bg-slate-800/30 rounded-3xl border border-slate-700/50 max-w-lg mx-auto my-12 space-y-6 shadow-2xl animate-fade-in relative overflow-hidden">
                            <div className="absolute -top-12 -left-12 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl"></div>
                            <div className="absolute -bottom-12 -right-12 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl"></div>
                            
                            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-400 rounded-3xl flex items-center justify-center shadow-lg shadow-orange-500/20 relative">
                                <Lock className="w-10 h-10 text-slate-900" />
                            </div>
                            
                            <div className="space-y-2 relative z-10">
                                <h2 className="text-2xl font-black text-white tracking-tight">Período de Acesso Expirado 🔒</h2>
                                <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
                                    Seu período de teste gratuito ou assinatura ativa chegou ao fim. Para continuar usando o Driver Score de forma ilimitada, ative ou renove sua assinatura.
                                </p>
                            </div>
                            
                            <div className="w-full h-px bg-slate-700/50 my-2"></div>
                            
                            <div className="w-full space-y-3 relative z-10">
                                <button
                                    onClick={() => handleRequirePremium('Assinatura Mensal')}
                                    className="w-full py-4 px-6 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 active:scale-95 text-white font-black rounded-2xl shadow-lg shadow-orange-600/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <Crown className="w-5 h-5" />
                                    Ativar Assinatura Premium
                                </button>
                                <button
                                    onClick={logoutUser}
                                    className="w-full py-3 px-6 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-slate-200 font-bold rounded-2xl transition-all flex items-center justify-center gap-2 border border-slate-700/50 cursor-pointer"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Sair da Conta
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {view === ViewMode.DASHBOARD && (
                                <>
                                    {/* Garage Audit Cards */}
                                    <GarageDriverCard
                                        checkins={garageCheckins}
                                        userId={session?.user?.id || ''}
                                        userProfile={userProfile}
                                        onUpdate={async () => {
                                            const todayObj = new Date();
                                            const todayStr = todayObj.toISOString().split('T')[0];
                                            const yesterdayObj = new Date(todayObj);
                                            yesterdayObj.setDate(yesterdayObj.getDate() - 1);
                                            const yesterdayStr = yesterdayObj.toISOString().split('T')[0];

                                            const { data } = await supabase
                                                .from('garage_checkins')
                                                .select('*')
                                                .eq('user_id', session?.user?.id)
                                                .in('shift_date', [yesterdayStr, todayStr]);

                                            if (data) {
                                                const activeCheckins = data.filter(c => c.status !== 'COMPLETED');
                                                setGarageCheckins(activeCheckins);
                                            }
                                        }}
                                    />
                                    <div id="tour-marketplace">
                                        <Stories
                                            isPremiumOrVip={isPremiumOrVip}
                                            onRequirePremium={handleRequirePremium}
                                            onViewProfile={(id) => {
                                                setViewingProfileId(id);
                                                setView(ViewMode.PROFILE);
                                            }}
                                        />
                                    </div>
                                    <Dashboard
                                        shifts={shifts}
                                        isLoading={isLoading}
                                        onAdvanceClick={() => changeView(ViewMode.FINANCE)}
                                        onPlanWeek={() => changeView(ViewMode.ADD_SHIFT)}
                                        isPrivacyMode={isPrivacyMode}
                                        setIsPrivacyMode={setIsPrivacyMode}
                                        userProfile={userProfile}
                                        tourStep={tourStep}
                                        setTourStep={setTourStep}
                                        isPremiumOrVip={isPremiumOrVip}
                                    />
                                </>
                            )}
                            {view === ViewMode.FINANCE && (
                                <AdvanceCalculator
                                    onClose={() => setView(ViewMode.DASHBOARD)}
                                    onConfirm={(newShift) => handleShiftSubmit(newShift)}
                                    shifts={shifts}
                                    isPrivacyMode={isPrivacyMode}
                                    currentBalance={shifts.reduce((acc, s) => {
                                        const now = new Date();
                                        const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                                        if (!s.date || !s.date.startsWith(currentMonthStr)) return acc;

                                        const earnings = (s.totalEarnings || 0) + (s.appTips || 0) + (s.tips || 0) + (s.extraEarnings?.reduce((a, b) => a + b.amount, 0) || 0);
                                        const expenses = s.expenses?.reduce((a, b) => a + b.amount, 0) || 0;
                                        return acc + earnings - expenses;
                                    }, 0)}
                                    totalAccumulatedGross={shifts.reduce((acc, s) => {
                                        const earnings = (s.totalEarnings || 0) + (s.appTips || 0) + (s.tips || 0) + (s.extraEarnings?.reduce((a, b) => a + b.amount, 0) || 0);
                                        return acc + earnings;
                                    }, 0)}
                                    userId={session?.user.id || ''}
                                />
                            )}
                            {view === ViewMode.FLEET && (
                                <FleetView userEmail={session.user.email} isAdmin={isPremiumOrVip} />
                            )}
                            {view === ViewMode.ADD_SHIFT && (
                                <ShiftEntry
                                    onSave={handleShiftSubmit}
                                    initialData={formInitialData || undefined}
                                    existingShifts={shifts}
                                    isPremiumOrVip={isPremiumOrVip}
                                    onRequirePremium={handleRequirePremium}
                                    userGeneration={userProfile?.generation || 'OLD'}
                                    userProfile={userProfile}
                                    onProfileUpdate={() => session?.user?.id && fetchUserData(session.user.id)}
                                />
                            )}
                            {view === ViewMode.HISTORY && (
                                <HistoryView
                                    shifts={shifts}
                                    onDelete={handleDeleteShift}
                                    onEdit={handleEditShift}
                                    onConfirmAdjust={(newShift) => handleShiftSubmit(newShift, false)}
                                    onDuplicate={handleDuplicateShift}
                                    isLoading={isLoading}
                                    isPrivacyMode={isPrivacyMode}
                                    userGeneration={userProfile?.generation || 'OLD'}
                                    isPremiumOrVip={isPremiumOrVip}
                                    onRequirePremium={handleRequirePremium}
                                />
                            )}
                            {view === ViewMode.AI_AVATAR && (
                                <AIAvatarGenerator 
                                    userProfile={userProfile}
                                    onBack={() => changeView(ViewMode.PROFILE)}
                                    onUpdateProfile={(newAvatarUrl) => {
                                        // Refresh user data to get updated profile
                                        fetchUserData(session.user.id);
                                        changeView(ViewMode.PROFILE);
                                    }}
                                />
                            )}
                            {view === ViewMode.FEEDBACK && (
                                <Feedback userEmail={session.user.email} />
                            )}
                            {view === ViewMode.PROFILE && (
                                <ProfileView
                                    userProfile={userProfile}
                                    currentXP={userProfile?.xp_points || 0}
                                    onProfileUpdate={() => fetchUserData(session.user.id)}
                                    viewingProfileId={viewingProfileId}
                                    onRestartTour={startTour}
                                    tourState={{ tourStep, setTourStep }}
                                    onOpenAIGenerator={() => changeView(ViewMode.AI_AVATAR)}
                                    theme={theme}
                                    onThemeChange={setTheme}
                                />
                            )}
                            {view === ViewMode.PARTNERS && (
                                <PartnersHub
                                    userProfile={userProfile}
                                    isPremiumOrVip={isPremiumOrVip}
                                    onRequirePremium={handleRequirePremium}
                                    onViewProfile={(id) => {
                                        setViewingProfileId(id);
                                        changeView(ViewMode.PROFILE);
                                    }}
                                />
                            )}
                            {view === ViewMode.SUPPORT_CENTER && (
                                <SupportCenter />
                            )}
                            {view === ViewMode.RANKING && (
                                <RankingView
                                    currentUserId={session.user.id}
                                    isPrivacyMode={isPrivacyMode}
                                    onViewProfile={(id) => {
                                        setViewingProfileId(id);
                                        changeView(ViewMode.PROFILE);
                                    }}
                                />
                            )}
                            {view === ViewMode.PERFORMANCE && (
                                <PerformanceView
                                    shifts={shifts}
                                    userProfile={userProfile}
                                    isPrivacyMode={isPrivacyMode}
                                    isGoogleConnected={!!isGoogleConnected}
                                    hasProviderToken={!!getProviderToken()}
                                    onConnectGoogle={handleConnectGoogle}
                                    onProfileUpdate={() => session?.user?.id && fetchUserData(session.user.id)}
                                    isPremiumOrVip={isPremiumOrVip}
                                />
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Global Alarm Overlay */}
            {isAlarmRinging && (
                <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center animate-fade-in p-8 text-center">
                    <div className="relative w-full max-w-md bg-gradient-to-br from-indigo-900 to-purple-900 rounded-3xl p-8 flex flex-col items-center justify-center border-4 border-white/10 shadow-[0_0_100px_rgba(124,58,237,0.5)] overflow-hidden">
                        {/* Background Pulsing loaders */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none">
                            <Volume2 className="w-full h-full animate-pulse" />
                        </div>

                        <Loader2 className="w-16 h-16 text-white opacity-80 mb-6 animate-spin relative z-10" />

                        <h2 className="text-2xl font-bold text-white mb-2 relative z-10">Despertador Driver Score</h2>

                        {/* Motivational Message */}
                        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 mb-6 border border-white/10 relative z-10 max-w-[280px]">
                            <p className="text-white text-lg font-medium leading-tight">
                                "Acorda, <span className="text-orange-400 font-bold">{userProfile?.nickname || 'Motorista'}</span>! <br />
                                Você é <span className="text-amber-400 font-bold">{((userProfile?.rhino_score ?? 100) >= 95 ? 'Ouro' : (userProfile?.rhino_score ?? 100) >= 80 ? 'Prata' : 'Bronze')}</span>. A escala abre em breve. <br />
                                <span className="text-blue-400 mt-2 block">Vamos pra cima!"</span>
                            </p>
                        </div>

                        <p className="text-purple-300 text-xs font-mono tracking-widest uppercase mb-1 relative z-10">Playlist Projeto</p>
                        <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-600 mb-6 tracking-tighter relative z-10">
                            {activeAlarmTrack?.label || 'DSAudio'} - ROMANOSV3
                        </h3>

                        {/* YouTube Audio Player and Interaction Button */}
                        <div className="w-full space-y-4 relative z-10">
                            {!hasInteractedWithAlarm ? (
                                <button
                                    onClick={() => setHasInteractedWithAlarm(true)}
                                    className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-2xl flex items-center justify-center gap-3 animate-bounce shadow-xl shadow-red-600/20 active:scale-95 transition-all text-lg"
                                >
                                    <Play className="w-6 h-6 fill-white" />
                                    DAR PLAY NA TRACK 🔊
                                </button>
                            ) : (
                                <div className="w-full aspect-video rounded-xl overflow-hidden shadow-2xl bg-black border border-white/10">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={`https://www.youtube.com/embed/${activeAlarmTrack?.ytId || 'iXAd5894BPw'}?autoplay=1&controls=0&modestbranding=1&rel=0`}
                                        title="YouTube Alarm Audio"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            )}

                            {/* Actions row */}
                            <div className="grid grid-cols-2 gap-3 w-full">
                                <a
                                    href="https://71f79915054912.la.deputy.com/#/home"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-orange-500/20 active:scale-95 text-xs uppercase tracking-wider"
                                >
                                    Fazer Escala
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                                <button
                                    onClick={() => {
                                        const shareUrl = `https://youtu.be/${activeAlarmTrack?.ytId}`;
                                        if (navigator.share) {
                                            navigator.share({ title: 'Curti essa track no Driver Score!', url: shareUrl });
                                        } else {
                                            navigator.clipboard.writeText(shareUrl);
                                            alert('Link da música copiado para o grupo!');
                                        }
                                    }}
                                    className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-blue-500/20 active:scale-95 text-xs uppercase tracking-wider"
                                >
                                    Compartilhar
                                    <Share2 className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            setIsAlarmRinging(false);
                            setHasInteractedWithAlarm(false);
                        }}
                        className="mt-8 bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-full p-4 px-10 backdrop-blur-md transition-all font-bold text-sm"
                    >
                        Parar Despertador
                    </button>
                </div>
            )}

            <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 px-4 py-2 z-40 flex justify-between items-center pb-[calc(1rem+env(safe-area-inset-bottom))]">
                <button onClick={() => changeView(ViewMode.DASHBOARD)} className={`flex flex-col items-center p-2 rounded-lg ${view === ViewMode.DASHBOARD ? 'text-orange-500' : 'text-slate-500'}`}>
                    <LayoutDashboard className="w-6 h-6" />
                    <span className="text-[10px] mt-1 font-medium">Início</span>
                </button>
                <button onClick={() => changeView(ViewMode.FINANCE)} className={`flex flex-col items-center p-2 rounded-lg ${view === ViewMode.FINANCE ? 'text-orange-500' : 'text-slate-500'}`}>
                    <Wallet className="w-6 h-6" />
                    <span className="text-[10px] mt-1 font-medium">Finanças</span>
                </button>
                <button onClick={() => changeView(ViewMode.ADD_SHIFT)} className={`flex flex-col items-center p-2 rounded-lg ${view === ViewMode.ADD_SHIFT ? 'text-orange-500' : 'text-slate-500'}`}>
                    <PlusCircle className="w-6 h-6" />
                    <span className="text-[10px] mt-1 font-medium">Programados</span>
                </button>
                <button onClick={() => changeView(ViewMode.FLEET)} className={`flex flex-col items-center p-2 rounded-lg ${view === ViewMode.FLEET ? 'text-orange-500' : 'text-slate-500'}`}>
                    <Car className="w-6 h-6" />
                    <span className="text-[10px] mt-1 font-medium">Mapa</span>
                </button>
                <button onClick={() => changeView(ViewMode.RANKING)} className={`flex flex-col items-center p-2 rounded-lg ${view === ViewMode.RANKING ? 'text-orange-500' : 'text-slate-500'}`}>
                    <Trophy className="w-6 h-6" />
                    <span className="text-[10px] mt-1 font-medium">Rank</span>
                </button>
                <button onClick={() => { setViewingProfileId(null); changeView(ViewMode.PROFILE); }} className={`flex flex-col items-center p-2 rounded-lg ${view === ViewMode.PROFILE ? 'text-orange-500' : 'text-slate-500'}`}>
                    <User className="w-6 h-6" />
                    <span className="text-[10px] mt-1 font-medium">Perfil</span>
                </button>
            </nav>

            <div className="h-6 md:hidden"></div>

            {/* Rhino Error Modal */}
            {showRhinoError && (
                <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-3xl p-8 max-w-sm w-full shadow-2xl transform animate-scale-in">
                        <div className="text-4xl mb-4">😅</div>
                        <h3 className="text-2xl font-black text-white mb-4 tracking-tight">Putz, amigo ou amiga!</h3>

                        <div className="space-y-4 text-slate-300 leading-relaxed">
                            <p>
                                Como você está logado com uma conta <span className="text-orange-400 font-bold">não-Google</span>, eu não consigo dar aquele pulo automático na sua caixa de entrada pra ler sua pontuação.
                            </p>
                            <p>
                                Por enquanto, esse "mimo" de ler o e-mail sozinho só funciona com o Gmail.
                            </p>
                            <p className="bg-slate-900/50 p-3 rounded-xl border border-slate-700/50 text-sm italic">
                                Mas relaxa! Você não vai ficar na mão: <span className="text-white font-medium">pode ajustar sua moldura e o alarme manualmente</span> aqui mesmo no app.
                            </p>
                        </div>

                        <button
                            onClick={() => {
                                setShowRhinoError(false);
                                localStorage.setItem('rhino_error_shown', new Date().toDateString());
                            }}
                            className="w-full mt-8 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-black py-4 rounded-2xl transition-all shadow-lg shadow-orange-500/20 active:scale-95"
                        >
                            Beleza, entendi!
                        </button>
                    </div>
                </div>
            )}
            <ZereSeuAnoModal
                isOpen={isZereSeuAnoModalOpen}
                onClose={() => setIsZereSeuAnoModalOpen(false)}
                userProfile={userProfile}
            />
            <TuesdayShieldModal
                isOpen={isTuesdayModalOpen}
                onClose={() => setIsTuesdayModalOpen(false)}
                onSubscribe={() => handleRequirePremium('Assinatura e Promoção de Indicação')}
                onGoToProfile={() => {
                    setIsTuesdayModalOpen(false);
                    changeView(ViewMode.PROFILE);
                }}
                userGeneration={userProfile?.generation || 'OLD'}
            />
            <PwaTipModal isOpen={showPwaTip} onClose={() => setShowPwaTip(false)} />
            <SubscriptionModal 
                isOpen={isGlobalSubModalOpen} 
                onClose={() => setIsGlobalSubModalOpen(false)} 
                user={userProfile} 
                featureName={globalSubFeatureName} 
                onSuccess={() => {
                    setIsGlobalSubModalOpen(false);
                    if (session?.user?.id) {
                        fetchUserData(session.user.id);
                    }
                }} 
            />
        </div>
    );
};

export default App;
